import { Injectable, signal } from '@angular/core';
import { VaultSyncWriter } from './vault-sync.tokens';
import { Log } from '../../core/log';
import { IS_ELECTRON } from '../../app.constants';

/**
 * Electron implementation of VaultSyncWriter.
 *
 * Routes filesystem writes through the IPC bridge so the renderer process
 * doesn't touch fs.* directly. The main process listens on the
 * 'vault-sync:write-file' / 'vault-sync:read-file' / 'vault-sync:delete' /
 * 'vault-sync:watch' channels.
 *
 * IPC handlers must be implemented in electron/ipc-handlers/vault-sync.ts
 * (WS4 sprint 3 — the bridge wiring lives outside this commit).
 *
 * Vault root path is configured via Settings → Vault path. Stored in
 * IndexedDB / localStorage; read on service init.
 */
@Injectable()
export class VaultSyncWriterElectron implements VaultSyncWriter {
  readonly vaultPath = signal<string>('');

  isSupported(): boolean {
    return IS_ELECTRON;
  }

  async writeFile(relativePath: string, content: string): Promise<void> {
    if (!this.isSupported()) {
      throw new Error('[vault-sync] Electron not detected');
    }
    if (!this.vaultPath()) {
      throw new Error('[vault-sync] Vault path not configured');
    }
    const ipc = this._getIpc();
    if (!ipc) throw new Error('[vault-sync] IPC bridge unavailable');
    await ipc.invoke('vault-sync:write-file', {
      vaultRoot: this.vaultPath(),
      relativePath,
      content,
    });
  }

  async readFile(relativePath: string): Promise<string | null> {
    if (!this.isSupported() || !this.vaultPath()) return null;
    const ipc = this._getIpc();
    if (!ipc) return null;
    try {
      const result = await ipc.invoke('vault-sync:read-file', {
        vaultRoot: this.vaultPath(),
        relativePath,
      });
      return typeof result === 'string' ? result : null;
    } catch {
      return null;
    }
  }

  async deleteFile(relativePath: string): Promise<void> {
    if (!this.isSupported() || !this.vaultPath()) return;
    const ipc = this._getIpc();
    if (!ipc) return;
    try {
      await ipc.invoke('vault-sync:delete', {
        vaultRoot: this.vaultPath(),
        relativePath,
      });
    } catch (err) {
      Log.warn({
        id: 'vault-sync.delete-failed',
        msg: (err as Error).message,
      });
    }
  }

  watch(
    handler: (relativePath: string, change: 'created' | 'modified' | 'deleted') => void,
  ): () => void {
    if (!this.isSupported() || !this.vaultPath()) return () => undefined;
    const ipc = this._getIpc();
    if (!ipc || !(ipc as any).on) return () => undefined;
    const ipcOn = (ipc as any).on as (
      channel: string,
      cb: (...args: any[]) => void,
    ) => void;
    const ipcOff = (ipc as any).removeListener as (
      channel: string,
      cb: (...args: any[]) => void,
    ) => void;
    const handlerWrapper = (_evt: unknown, payload: any): void => {
      if (payload?.relativePath && payload?.change) {
        handler(payload.relativePath, payload.change);
      }
    };
    ipcOn('vault-sync:file-change', handlerWrapper);
    ipc.invoke('vault-sync:watch-start', {
      vaultRoot: this.vaultPath(),
    });
    return () => {
      ipcOff?.('vault-sync:file-change', handlerWrapper);
      ipc.invoke('vault-sync:watch-stop').catch(() => undefined);
    };
  }

  private _getIpc(): {
    invoke: (channel: string, ...args: any[]) => Promise<any>;
  } | null {
    if (typeof window === 'undefined') return null;
    const electron = (window as any).electron;
    return electron?.ipcRenderer ?? null;
  }
}
